import { formatPhoneDisplay, isPhoneComplete, normalizePhone } from "./phoneUtils";

const normalizePhoneEntry = (entry) => {
  const normalized = normalizePhone(entry);
  return {
    type: entry?.type?.trim() || "mobile",
    countryCode: normalized.countryCode,
    areaCode: normalized.areaCode,
    number: normalized.number,
    isDefault: Boolean(entry?.isDefault),
    tags: Array.isArray(entry?.tags) ? entry.tags : [],
  };
};

const normalizeEmailEntry = (entry) => ({
  type: entry?.type?.trim() || "personal",
  value: entry?.value?.trim() ?? "",
  isDefault: Boolean(entry?.isDefault),
  tags: Array.isArray(entry?.tags) ? entry.tags : [],
});

const normalizeLegacyAddress = (address) => {
  if (address == null || address === "") return null;

  if (typeof address === "string") {
    const line1 = address.trim();
    if (!line1) return null;
    return {
      type: "correspondence",
      line1,
      line2: "",
      city: "Hong Kong",
      state: "",
      zipCode: "",
      country: "Hong Kong",
      tags: [],
      isDefault: true,
    };
  }

  if (typeof address === "object") {
    const line1 = address.line1?.trim() ?? "";
    const line2 = address.line2?.trim() ?? "";
    if (!line1 && !line2) return null;
    return {
      type: address.type?.trim() || "correspondence",
      line1,
      line2,
      city: address.city?.trim() || "Hong Kong",
      state: address.state?.trim() ?? "",
      zipCode: address.zipCode?.trim() ?? "",
      country: address.country?.trim() || "Hong Kong",
      tags: Array.isArray(address.tags) ? address.tags : [],
      isDefault: Boolean(address.isDefault) || true,
    };
  }

  return null;
};

const isEmergencyPhone = (entry) =>
  String(entry?.type ?? "").trim().toLowerCase() === "emergency";

const phoneMatches = (left, right) => {
  const a = normalizePhone(left);
  const b = normalizePhone(right);
  return (
    a.countryCode === b.countryCode &&
    a.areaCode === b.areaCode &&
    a.number === b.number
  );
};

const hasMatchingPhone = (phones, legacyPhone) =>
  phones.some(
    (entry) => isPhoneComplete(entry) && phoneMatches(entry, legacyPhone)
  );

const appendLegacyPhone = (phones, legacyPhone, type, isDefault) => {
  if (!isPhoneComplete(legacyPhone) || hasMatchingPhone(phones, legacyPhone)) {
    return phones;
  }
  if (type === "emergency" && phones.some(isEmergencyPhone)) {
    return phones;
  }
  return [
    ...phones,
    normalizePhoneEntry({
      type,
      ...normalizePhone(legacyPhone),
      isDefault,
    }),
  ];
};

const mergeLegacyPhones = (phones, legacy) => {
  let next = [...phones];
  const nonEmergency = () => next.filter((phone) => !isEmergencyPhone(phone));

  next = appendLegacyPhone(next, legacy.phone, "mobile", nonEmergency().length === 0);
  next = appendLegacyPhone(
    next,
    legacy.phone2,
    "other",
    nonEmergency().length === 0
  );
  next = appendLegacyPhone(next, legacy.emergencyContactNo, "emergency", false);
  return next;
};

export const hydrateUserContacts = (user) => {
  if (!user || typeof user !== "object") return user;

  const next = { ...user };
  const phones = Array.isArray(next.phones) ? [...next.phones] : [];
  const emails = Array.isArray(next.emails) ? [...next.emails] : [];
  const addresses = Array.isArray(next.addresses) ? [...next.addresses] : [];
  const loginEmail = next.email?.trim().toLowerCase();

  next.phones = mergeLegacyPhones(phones, next);

  if (emails.length === 0 && next.personalEmail?.trim()) {
    emails.push(
      normalizeEmailEntry({
        type: "personal",
        value: next.personalEmail,
        isDefault: true,
      })
    );
  }

  if (addresses.length === 0) {
    const legacyAddress = normalizeLegacyAddress(next.address);
    if (legacyAddress) {
      addresses.push(legacyAddress);
    }
  }

  next.phones = next.phones.filter((entry) => isPhoneComplete(entry));
  next.emails = emails.filter(
    (entry) =>
      entry.value?.trim() &&
      entry.value.trim().toLowerCase() !== loginEmail
  );
  next.addresses = addresses.filter(
    (entry) => entry?.line1?.trim() || entry?.line2?.trim()
  );

  const nonEmergency = next.phones.filter((phone) => !isEmergencyPhone(phone));
  if (nonEmergency.length && !nonEmergency.some((phone) => phone.isDefault)) {
    nonEmergency[0].isDefault = true;
  }
  if (next.emails.length && !next.emails.some((email) => email.isDefault)) {
    next.emails[0].isDefault = true;
  }

  return next;
};

export const getDefaultPhone = (user) => {
  if (!user) return null;
  const hydrated = hydrateUserContacts(user);
  const nonEmergency = (hydrated.phones ?? []).filter(
    (phone) => !isEmergencyPhone(phone)
  );
  const fromArray =
    nonEmergency.find((phone) => phone?.isDefault) ?? nonEmergency[0];
  if (fromArray && isPhoneComplete(fromArray)) {
    return normalizePhone(fromArray);
  }
  if (isPhoneComplete(hydrated.phone)) {
    return normalizePhone(hydrated.phone);
  }
  return null;
};

export const getDefaultEmail = (user) => user?.email?.trim() ?? "";

export const getEmergencyPhone = (user) => {
  if (!user) return null;
  const hydrated = hydrateUserContacts(user);
  const fromArray = hydrated.phones?.find(
    (phone) => isEmergencyPhone(phone) && isPhoneComplete(phone)
  );
  if (fromArray) return normalizePhone(fromArray);
  if (isPhoneComplete(hydrated.emergencyContactNo)) {
    return normalizePhone(hydrated.emergencyContactNo);
  }
  return null;
};

export const formatDefaultPhoneDisplay = (user) => {
  const phone = getDefaultPhone(user);
  return phone ? formatPhoneDisplay(phone) : "";
};

export const formatEmergencyPhoneDisplay = (user) => {
  const phone = getEmergencyPhone(user);
  return phone ? formatPhoneDisplay(phone) : "";
};
